import axios, { AxiosInstance, AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { Organization } from "./models/organization.ts";
import { APIKey, APIKeyCreate, APIKeyUpdate } from "./models/apiKey.ts";
import { BucketConfig, BucketConfigCreate, BucketConfigUpdate } from "./models/bucketConfig.ts";
import { ChatConversation, ChatConversationCreate, ChatConversationUpdate } from './models/chat';
import { TmRisk, TmRiskUpdate } from "./modules/threatModeling/models/tmRisk.ts";
import { TmMitigation, TmMitigationUpdate } from "./modules/threatModeling/models/tmMitigation.ts";
import { TmActor, TmActorUpdate } from "./modules/threatModeling/models/tmActor.ts";
import { TmAssessment, TmAssessmentUpdate } from "./modules/threatModeling/models/tmAssessment.ts";
import { DoraGeneralInfo, DoraGeneralInfoUpdate } from "./modules/dora/models/doraGeneralInfo.ts";
import { DoraFinancialEntity, DoraFinancialEntityUpdate } from "./modules/dora/models/doraFinancialEntity.ts";
import { DoraBusinessService, DoraBusinessServiceUpdate } from "./modules/dora/models/doraBusinessService.ts";
import { DoraFunction, DoraFunctionUpdate } from "./modules/dora/models/doraFunction.ts";
import { DoraICTThirdPartyProvider, DoraICTThirdPartyProviderUpdate } from "./modules/dora/models/doraICTThirdPartyProvider.ts";
import { DoraICTService, DoraICTServiceUpdate } from "./modules/dora/models/doraICTService.ts";
import { DoraControl, DoraControlUpdate } from "./modules/dora/models/doraControl.ts";
import { DoraRisk, DoraRiskUpdate } from "./modules/dora/models/doraRisk.ts";
import { DoraIncident, DoraIncidentUpdate } from "./modules/dora/models/doraIncident.ts";
import { DoraTestingExercise, DoraTestingExerciseUpdate } from "./modules/dora/models/doraTestingExercise.ts";
import { DoraFinding, DoraFindingUpdate } from "./modules/dora/models/doraFinding.ts";
import { DoraContractualArrangement, DoraContractualArrangementUpdate } from "./modules/dora/models/doraContractualArrangement.ts";
import { DoraContractualArrangementDetail, DoraContractualArrangementDetailUpdate } from "./modules/dora/models/doraContractualArrangementDetail.ts";
import { DoraIctServiceAssessment, DoraIctServiceAssessmentUpdate } from "./modules/dora/models/doraIctServiceAssessment.ts";
import { getKeycloakInstance } from './keycloak.ts';

const hostname: string = window.location.hostname;
console.log("hostname:" + hostname)
const isLocalhost: boolean = Boolean(
  hostname === 'localhost' ||
  hostname === '127.0.0.1' ||
  hostname === '[::1]' ||
  // Check for any IPv4 loopback addresses like 127.x.x.x
  /^127(?:\.\d+){0,3}$/.test(hostname)
);

let baseURL: string = 'http://localhost:8080';
if (!isLocalhost) {
  baseURL = 'https://api.risky.kubemaster.com';
}

console.log('🌐 API Configuration:', {
  hostname,
  isLocalhost,
  baseURL,
  windowLocation: window.location.href
});

// Create Axios instance
export const api: AxiosInstance = axios.create({
    baseURL,
    headers: {
        'Content-Type': 'application/json'
    },
});

api.interceptors.request.use((request: InternalAxiosRequestConfig) => {
  console.log('🔍 API Request Debug:', {
    baseURL: request.baseURL,
    url: request.url,
    fullURL: `${request.baseURL}${request.url}`,
    hostname: window.location.hostname,
    isLocalhost,
  });

  // Automatically add organization_id to all requests
  const currentOrgId = localStorage.getItem('currentOrganizationId');
  if (currentOrgId) {
    request.params = {
      ...request.params,
      organization_id: currentOrgId
    };
  }

  return request;
});

// Response interceptor to handle token refresh on 401 errors
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config;

    // Check if error is 401 and we haven't already tried to refresh
    if (error.response?.status === 401 && originalRequest && !(originalRequest as any)._retry) {
      (originalRequest as any)._retry = true;

      try {
        console.log('🔄 Token expired, attempting to refresh...');
        const keycloak = getKeycloakInstance();

        // Try to refresh the token (minValidity = 5 seconds)
        const refreshed = await keycloak.updateToken(5);

        if (refreshed && keycloak.token) {
          console.log('✅ Token refreshed successfully');
          // Update the Authorization header with the new token
          setAuthToken(keycloak.token);
          originalRequest.headers['Authorization'] = `Bearer ${keycloak.token}`;

          // Retry the original request
          return api(originalRequest);
        } else {
          console.log('⚠️ Token refresh returned false, redirecting to login');
          keycloak.login();
          return Promise.reject(error);
        }
      } catch (refreshError) {
        console.error('❌ Token refresh failed:', refreshError);
        // Token refresh failed, redirect to login
        const keycloak = getKeycloakInstance();
        keycloak.login();
        return Promise.reject(error);
      }
    }

    // For all other errors, just reject
    return Promise.reject(error);
  }
);

export const setAuthToken = (token: string | null): void => {
    if (token) {
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
        delete api.defaults.headers.common['Authorization'];
    }
};

// Threat Modeling Assessments API functions
export const listTmAssessments = async (): Promise<TmAssessment[]> => {
  const response: AxiosResponse<TmAssessment[]> = await api.get<TmAssessment[]>('/tm/assessments');
  return response.data;
};

export const createTmAssessment = async (assessmentData: Omit<TmAssessment, 'id'>): Promise<TmAssessment> => {
  const response: AxiosResponse<TmAssessment> = await api.post<TmAssessment>('/tm/assessments', assessmentData);
  return response.data;
};

export const updateTmAssessmentById = async (
  id: number,
  assessmentData: TmAssessmentUpdate,
): Promise<TmAssessment> => {
  const response: AxiosResponse<TmAssessment> = await api.put<TmAssessment>(`/tm/assessments/${id}`, assessmentData);
  return response.data;
};

export const deleteTmAssessmentById = async (id: number): Promise<void> => {
  await api.delete<void>(`/tm/assessments/${id}`);
};

// Threat Modeling API functions
export const createTmRisk = async (riskData: Omit<TmRisk, 'id'>): Promise<TmRisk> => {
  const response: AxiosResponse<TmRisk> = await api.post<TmRisk>('/tm/risks', riskData);
  return response.data;
};

export const updateTmRiskById = async (id: number, riskData: TmRiskUpdate): Promise<TmRisk> => {
  const response: AxiosResponse<TmRisk> = await api.put<TmRisk>(`/tm/risks/${id}`, riskData);
  return response.data;
};

export const deleteTmRiskById = async (id: number): Promise<void> => {
  await api.delete<void>(`/tm/risks/${id}`);
};

export const listTmRisks = async (assessmentId?: number): Promise<TmRisk[]> => {
  const response: AxiosResponse<TmRisk[]> = await api.get<TmRisk[]>('/tm/risks', {
    params: assessmentId ? { assessment_id: assessmentId } : undefined,
  });
  return response.data;
};

export const getTmRisk = async (id: number): Promise<TmRisk> => {
  const response: AxiosResponse<TmRisk> = await api.get<TmRisk>(`/tm/risks/${id}`);
  return response.data;
};

export const createTmMitigation = async (mitigationData: Omit<TmMitigation, 'id'>): Promise<TmMitigation> => {
  const response: AxiosResponse<TmMitigation> = await api.post<TmMitigation>('/tm/mitigations', mitigationData);
  return response.data;
};

export const listTmMitigations = async (assessmentId?: number): Promise<TmMitigation[]> => {
  const response = await api.get<TmMitigation[]>('/tm/mitigations', {
    params: assessmentId ? { assessment_id: assessmentId } : undefined,
  });
  return response.data;
};

export const updateTmMitigationById = async (id: number, mitigationData: TmMitigationUpdate): Promise<TmMitigation> => {
  const response: AxiosResponse<TmMitigation> = await api.put<TmMitigation>(`/tm/mitigations/${id}`, mitigationData);
  return response.data;
};

export const deleteTmMitigationById = async (id: number): Promise<void> => {
  await api.delete<void>(`/tm/mitigations/${id}`);
};

export const createTmActor = async (actorData: Omit<TmActor, 'id'>): Promise<TmActor> => {
  const response: AxiosResponse<TmActor> = await api.post<TmActor>('/tm/actors', actorData);
  return response.data;
};

export const listTmActors = async (assessmentId?: number): Promise<TmActor[]> => {
  const response: AxiosResponse<TmActor[]> = await api.get<TmActor[]>('/tm/actors', {
    params: assessmentId ? { assessment_id: assessmentId } : undefined,
  });
  return response.data;
};

export const updateTmActorById = async (id: number, actorData: TmActorUpdate): Promise<TmActor> => {
  const response: AxiosResponse<TmActor> = await api.put<TmActor>(`/tm/actors/${id}`, actorData);
  return response.data;
};

export const deleteTmActorById = async (id: number): Promise<void> => {
  await api.delete<void>(`/tm/actors/${id}`);
};

// Organization API functions

export const createOrganization = async (organizationData: Organization): Promise<Organization> => {
  const response: AxiosResponse<Organization> = await api.post<Organization>('/organizations', organizationData);
  return response.data;
};

// Multi-organization API functions
export interface OrganizationWithRole extends Organization {
  role: 'owner' | 'admin' | 'member' | 'viewer';
  is_default: boolean;
}

export const listMyOrganizations = async (): Promise<OrganizationWithRole[]> => {
  const response = await api.get<OrganizationWithRole[]>('/organizations/my');
  return response.data;
};

export const setDefaultOrganization = async (orgId: number): Promise<void> => {
  await api.post(`/organizations/${orgId}/set-default`);
};

export const updateOrganization = async (orgId: number, data: Partial<Organization>): Promise<Organization> => {
  const response = await api.put<Organization>(`/organizations/${orgId}`, data, {
    params: { organization_id: orgId }
  });
  return response.data;
};

export const addMemberToOrganization = async (
  orgId: number,
  email: string,
  role: string
): Promise<void> => {
  await api.post(`/organizations/${orgId}/members`, { email, role }, {
    params: { organization_id: orgId }
  });
};

export const removeMemberFromOrganization = async (
  orgId: number,
  userId: string
): Promise<void> => {
  await api.delete(`/organizations/${orgId}/members/${userId}`, {
    params: { organization_id: orgId }
  });
};

// DORA B_02 - Contractual Arrangements
export const getICTServiceTypes = async (): Promise<Array<{ code: string; description: string }>> => {
  const response = await api.get<Array<{ code: string; description: string }>>('/dora/contractual-arrangements/ict-service-types');
  return response.data;
};

export const listDoraArrangements = async (): Promise<DoraContractualArrangement[]> => {
  const response = await api.get<DoraContractualArrangement[]>('/dora/contractual-arrangements');
  return response.data;
};

export const createDoraArrangement = async (
  payload: Omit<DoraContractualArrangement, 'id' | 'organization_id'>,
): Promise<DoraContractualArrangement> => {
  const response = await api.post<DoraContractualArrangement>('/dora/contractual-arrangements', payload);
  return response.data;
};

export const updateDoraArrangement = async (
  id: number,
  payload: DoraContractualArrangementUpdate,
): Promise<DoraContractualArrangement> => {
  const response = await api.put<DoraContractualArrangement>(`/dora/contractual-arrangements/${id}`, payload);
  return response.data;
};

export const deleteDoraArrangement = async (id: number): Promise<void> => {
  await api.delete<void>(`/dora/contractual-arrangements/${id}`);
};

export const listDoraArrangementDetails = async (
  params?: { arrangement_id?: number; arrangement_reference?: string },
): Promise<DoraContractualArrangementDetail[]> => {
  const cleanParams: Record<string, any> = {};
  if (params?.arrangement_id !== undefined && params?.arrangement_id !== null) {
    cleanParams.arrangement_id = params.arrangement_id;
  }
  if (params?.arrangement_reference) {
    cleanParams.arrangement_reference = params.arrangement_reference;
  }

  const response = await api.get<DoraContractualArrangementDetail[]>('/dora/contractual-arrangements/details', {
    params: Object.keys(cleanParams).length ? cleanParams : undefined,
  });
  return response.data;
};

export const createDoraArrangementDetail = async (
  payload: Omit<DoraContractualArrangementDetail, 'id' | 'organization_id'>,
): Promise<DoraContractualArrangementDetail> => {
  const response = await api.post<DoraContractualArrangementDetail>('/dora/contractual-arrangements/details', payload);
  return response.data;
};

export const updateDoraArrangementDetail = async (
  id: number,
  payload: DoraContractualArrangementDetailUpdate,
): Promise<DoraContractualArrangementDetail> => {
  const response = await api.put<DoraContractualArrangementDetail>(`/dora/contractual-arrangements/details/${id}`, payload);
  return response.data;
};

export const deleteDoraArrangementDetail = async (id: number): Promise<void> => {
  await api.delete<void>(`/dora/contractual-arrangements/details/${id}`);
};

export interface OrganizationMember {
  user_id: string | null;
  name?: string | null;
  email?: string;
  role: string;
  is_default: boolean;
  pending?: boolean;
}

export const getOrganizationMembers = async (orgId: number): Promise<OrganizationMember[]> => {
  const response = await api.get<OrganizationMember[]>(`/organizations/${orgId}/members`, {
    params: { organization_id: orgId }
  });
  return response.data;
};

export const updateMemberRole = async (
  orgId: number,
  userId: string,
  role: string
): Promise<void> => {
  await api.put(`/organizations/${orgId}/members/${userId}/role`, { user_id: userId, role }, {
    params: { organization_id: orgId }
  });
};

// DORA: General Info (B_01.01)
export const listDoraGeneralInfo = async (): Promise<DoraGeneralInfo[]> => {
  const response = await api.get<DoraGeneralInfo[]>('/dora/general-info');
  return response.data;
};

export const createDoraGeneralInfo = async (data: Omit<DoraGeneralInfo, 'id'>): Promise<DoraGeneralInfo> => {
  const response = await api.post<DoraGeneralInfo>('/dora/general-info', data);
  return response.data;
};

export const getDoraGeneralInfo = async (id: number): Promise<DoraGeneralInfo> => {
  const response = await api.get<DoraGeneralInfo>(`/dora/general-info/${id}`);
  return response.data;
};

export const updateDoraGeneralInfo = async (
  id: number,
  data: DoraGeneralInfoUpdate
): Promise<DoraGeneralInfo> => {
  const response = await api.put<DoraGeneralInfo>(`/dora/general-info/${id}`, data);
  return response.data;
};

export const deleteDoraGeneralInfo = async (id: number): Promise<void> => {
  await api.delete(`/dora/general-info/${id}`);
};

// DORA: Financial Entities
export const listDoraFinancialEntities = async (): Promise<DoraFinancialEntity[]> => {
  const response = await api.get<DoraFinancialEntity[]>('/dora/financial-entities');
  return response.data;
};

export const createDoraFinancialEntity = async (data: Omit<DoraFinancialEntity, 'id'>): Promise<DoraFinancialEntity> => {
  const response = await api.post<DoraFinancialEntity>('/dora/financial-entities', data);
  return response.data;
};

export const updateDoraFinancialEntity = async (
  id: number,
  data: DoraFinancialEntityUpdate
): Promise<DoraFinancialEntity> => {
  const response = await api.put<DoraFinancialEntity>(`/dora/financial-entities/${id}`, data);
  return response.data;
};

export const deleteDoraFinancialEntity = async (id: number): Promise<void> => {
  await api.delete(`/dora/financial-entities/${id}`);
};

// DORA: Business Services
export const listDoraBusinessServices = async (): Promise<DoraBusinessService[]> => {
  const response = await api.get<DoraBusinessService[]>('/dora/business-services');
  return response.data;
};

export const createDoraBusinessService = async (data: Omit<DoraBusinessService, 'id'>): Promise<DoraBusinessService> => {
  const response = await api.post<DoraBusinessService>('/dora/business-services', data);
  return response.data;
};

export const updateDoraBusinessService = async (
  id: number,
  data: DoraBusinessServiceUpdate
): Promise<DoraBusinessService> => {
  const response = await api.put<DoraBusinessService>(`/dora/business-services/${id}`, data);
  return response.data;
};

export const deleteDoraBusinessService = async (id: number): Promise<void> => {
  await api.delete(`/dora/business-services/${id}`);
};

// DORA: Functions
export const listDoraFunctions = async (): Promise<DoraFunction[]> => {
  const response = await api.get<DoraFunction[]>('/dora/functions');
  return response.data;
};

export const createDoraFunction = async (data: Omit<DoraFunction, 'id' | 'function_identifier'>): Promise<DoraFunction> => {
  const response = await api.post<DoraFunction>('/dora/functions', data);
  return response.data;
};

export const updateDoraFunction = async (
  id: number,
  data: DoraFunctionUpdate
): Promise<DoraFunction> => {
  const response = await api.put<DoraFunction>(`/dora/functions/${id}`, data);
  return response.data;
};

export const deleteDoraFunction = async (id: number): Promise<void> => {
  await api.delete(`/dora/functions/${id}`);
};

export const getLicensedActivities = async (): Promise<string[]> => {
  const response = await api.get<string[]>('/dora/functions/licensed-activities');
  return response.data;
};

export const getCriticalityOptions = async (): Promise<string[]> => {
  const response = await api.get<string[]>('/dora/functions/criticality-options');
  return response.data;
};

export const getImpactOptions = async (): Promise<string[]> => {
  const response = await api.get<string[]>('/dora/functions/impact-options');
  return response.data;
};

// DORA: ICT Third-Party Providers
export const listDoraICTThirdPartyProviders = async (): Promise<DoraICTThirdPartyProvider[]> => {
  const response = await api.get<DoraICTThirdPartyProvider[]>('/dora/ict-third-party-providers');
  return response.data;
};

export const createDoraICTThirdPartyProvider = async (
  data: Omit<DoraICTThirdPartyProvider, 'id'>
): Promise<DoraICTThirdPartyProvider> => {
  const response = await api.post<DoraICTThirdPartyProvider>('/dora/ict-third-party-providers', data);
  return response.data;
};

export const updateDoraICTThirdPartyProvider = async (
  id: number,
  data: DoraICTThirdPartyProviderUpdate
): Promise<DoraICTThirdPartyProvider> => {
  const response = await api.put<DoraICTThirdPartyProvider>(`/dora/ict-third-party-providers/${id}`, data);
  return response.data;
};

export const deleteDoraICTThirdPartyProvider = async (id: number): Promise<void> => {
  await api.delete(`/dora/ict-third-party-providers/${id}`);
};

// DORA: ICT Services
export const listDoraICTServices = async (): Promise<DoraICTService[]> => {
  const response = await api.get<DoraICTService[]>('/dora/ict-services');
  return response.data;
};

export const createDoraICTService = async (
  data: Omit<DoraICTService, 'id'>
): Promise<DoraICTService> => {
  const response = await api.post<DoraICTService>('/dora/ict-services', data);
  return response.data;
};

export const updateDoraICTService = async (
  id: number,
  data: DoraICTServiceUpdate
): Promise<DoraICTService> => {
  const response = await api.put<DoraICTService>(`/dora/ict-services/${id}`, data);
  return response.data;
};

export const deleteDoraICTService = async (id: number): Promise<void> => {
  await api.delete(`/dora/ict-services/${id}`);
};

// DORA: Controls
export const listDoraControls = async (): Promise<DoraControl[]> => {
  const response = await api.get<DoraControl[]>('/dora/controls');
  return response.data;
};

export const createDoraControl = async (data: Omit<DoraControl, 'id'>): Promise<DoraControl> => {
  const response = await api.post<DoraControl>('/dora/controls', data);
  return response.data;
};

export const updateDoraControl = async (id: number, data: DoraControlUpdate): Promise<DoraControl> => {
  const response = await api.put<DoraControl>(`/dora/controls/${id}`, data);
  return response.data;
};

export const deleteDoraControl = async (id: number): Promise<void> => {
  await api.delete(`/dora/controls/${id}`);
};

// DORA: Risks
export const listDoraRisks = async (): Promise<DoraRisk[]> => {
  const response = await api.get<DoraRisk[]>('/dora/risks');
  return response.data;
};

export const createDoraRisk = async (data: Omit<DoraRisk, 'id'>): Promise<DoraRisk> => {
  const response = await api.post<DoraRisk>('/dora/risks', data);
  return response.data;
};

export const updateDoraRisk = async (id: number, data: DoraRiskUpdate): Promise<DoraRisk> => {
  const response = await api.put<DoraRisk>(`/dora/risks/${id}`, data);
  return response.data;
};

export const deleteDoraRisk = async (id: number): Promise<void> => {
  await api.delete(`/dora/risks/${id}`);
};

// DORA: Incidents
export const listDoraIncidents = async (): Promise<DoraIncident[]> => {
  const response = await api.get<DoraIncident[]>('/dora/incidents');
  return response.data;
};

export const createDoraIncident = async (data: Omit<DoraIncident, 'id'>): Promise<DoraIncident> => {
  const response = await api.post<DoraIncident>('/dora/incidents', data);
  return response.data;
};

export const updateDoraIncident = async (id: number, data: DoraIncidentUpdate): Promise<DoraIncident> => {
  const response = await api.put<DoraIncident>(`/dora/incidents/${id}`, data);
  return response.data;
};

export const deleteDoraIncident = async (id: number): Promise<void> => {
  await api.delete(`/dora/incidents/${id}`);
};

// DORA: Testing Exercises
export const listDoraTestingExercises = async (): Promise<DoraTestingExercise[]> => {
  const response = await api.get<DoraTestingExercise[]>('/dora/testing-exercises');
  return response.data;
};

export const createDoraTestingExercise = async (
  data: Omit<DoraTestingExercise, 'id'>
): Promise<DoraTestingExercise> => {
  const response = await api.post<DoraTestingExercise>('/dora/testing-exercises', data);
  return response.data;
};

export const updateDoraTestingExercise = async (
  id: number,
  data: DoraTestingExerciseUpdate
): Promise<DoraTestingExercise> => {
  const response = await api.put<DoraTestingExercise>(`/dora/testing-exercises/${id}`, data);
  return response.data;
};

export const deleteDoraTestingExercise = async (id: number): Promise<void> => {
  await api.delete(`/dora/testing-exercises/${id}`);
};

// DORA: Findings
export const listDoraFindings = async (): Promise<DoraFinding[]> => {
  const response = await api.get<DoraFinding[]>('/dora/findings');
  return response.data;
};

export const createDoraFinding = async (data: Omit<DoraFinding, 'id'>): Promise<DoraFinding> => {
  const response = await api.post<DoraFinding>('/dora/findings', data);
  return response.data;
};

export const updateDoraFinding = async (id: number, data: DoraFindingUpdate): Promise<DoraFinding> => {
  const response = await api.put<DoraFinding>(`/dora/findings/${id}`, data);
  return response.data;
};

export const deleteDoraFinding = async (id: number): Promise<void> => {
  await api.delete(`/dora/findings/${id}`);
};

// DORA: ICT Service Assessments (B_07.01)
export const listDoraIctServiceAssessments = async (): Promise<DoraIctServiceAssessment[]> => {
  const response = await api.get<DoraIctServiceAssessment[]>('/dora/ict-service-assessments');
  return response.data;
};

export const createDoraIctServiceAssessment = async (data: Omit<DoraIctServiceAssessment, 'id'>): Promise<DoraIctServiceAssessment> => {
  const response = await api.post<DoraIctServiceAssessment>('/dora/ict-service-assessments', data);
  return response.data;
};

export const updateDoraIctServiceAssessment = async (id: number, data: DoraIctServiceAssessmentUpdate): Promise<DoraIctServiceAssessment> => {
  const response = await api.put<DoraIctServiceAssessment>(`/dora/ict-service-assessments/${id}`, data);
  return response.data;
};

export const deleteDoraIctServiceAssessment = async (id: number): Promise<void> => {
  await api.delete(`/dora/ict-service-assessments/${id}`);
};

// API Key Management functions
export const listAPIKeys = async (): Promise<APIKey[]> => {
  const response = await api.get<APIKey[]>('/api-keys');
  return response.data;
};

export const createAPIKey = async (apiKeyData: APIKeyCreate): Promise<APIKey> => {
  const response = await api.post<APIKey>('/api-keys', apiKeyData);
  return response.data;
};

export const updateAPIKey = async (id: number, apiKeyData: APIKeyUpdate): Promise<APIKey> => {
  const response = await api.put<APIKey>(`/api-keys/${id}`, apiKeyData);
  return response.data;
};

export const deleteAPIKey = async (id: number): Promise<void> => {
  await api.delete(`/api-keys/${id}`);
};

// Bucket Configuration functions
export const listBucketConfigs = async (orgId: number): Promise<BucketConfig[]> => {
  const response = await api.get<BucketConfig[]>(`/bucket-configs/organizations/${orgId}/bucket-configs`, {
    params: { organization_id: orgId }
  });
  return response.data;
};

export const createBucketConfig = async (orgId: number, bucketConfigData: BucketConfigCreate): Promise<BucketConfig> => {
  const response = await api.post<BucketConfig>(`/bucket-configs/organizations/${orgId}/bucket-configs`, bucketConfigData, {
    params: { organization_id: orgId }
  });
  return response.data;
};

export const updateBucketConfig = async (orgId: number, configId: number, bucketConfigData: BucketConfigUpdate): Promise<BucketConfig> => {
  const response = await api.put<BucketConfig>(`/bucket-configs/organizations/${orgId}/bucket-configs/${configId}`, bucketConfigData, {
    params: { organization_id: orgId }
  });
  return response.data;
};

export const deleteBucketConfig = async (orgId: number, configId: number): Promise<void> => {
  await api.delete(`/bucket-configs/organizations/${orgId}/bucket-configs/${configId}`, {
    params: { organization_id: orgId }
  });
};

export interface BucketTestResult {
  success: boolean;
  message: string;
  bucket_name: string;
  bucket_type: string;
}

export const testBucketConfig = async (orgId: number, configId: number): Promise<BucketTestResult> => {
  const response = await api.post<BucketTestResult>(`/bucket-configs/organizations/${orgId}/bucket-configs/${configId}/test`, {}, {
    params: { organization_id: orgId }
  });
  return response.data;
};

// Chat API functions
export const listChatConversations = async (): Promise<ChatConversation[]> => {
  const response = await api.get<ChatConversation[]>('/chat/conversations');
  return response.data;
};

export const getChatConversation = async (id: number): Promise<ChatConversation> => {
  const response = await api.get<ChatConversation>(`/chat/conversations/${id}`);
  return response.data;
};

export const createChatConversation = async (data: ChatConversationCreate): Promise<ChatConversation> => {
  const response = await api.post<ChatConversation>('/chat/conversations', data);
  return response.data;
};

export const updateChatConversation = async (id: number, data: ChatConversationUpdate): Promise<ChatConversation> => {
  const response = await api.put<ChatConversation>(`/chat/conversations/${id}`, data);
  return response.data;
};

export const deleteChatConversation = async (id: number): Promise<void> => {
  await api.delete(`/chat/conversations/${id}`);
};

// Chat completions - streaming endpoint
// Note: This returns a streaming response, use with fetch API directly or EventSource
export const getChatCompletionsUrl = (): string => {
  return `${baseURL}/chat/completions`;
};
